import { useEffect, useState } from 'react';

import {
  FlightGuestBoxProps,
  FlightGuestProps,
} from '@components/views/flight/types';
import useGuestBoxErrorMessage from '@components/Guest/GuestBox/useGuestBoxErrorMessage';
import {
  ADULT_DEFAULT_NUMBER,
  ADULT_MAX_NUMBER,
  CHILD_DEFAULT_NUMBER,
  CHILD_MAX_NUMBER,
  getTotalGuestNumberErrorMessage,
  getGuestMaxNumberErrorMessage,
  INFANT_DEFAULT_NUMBER,
  INFANT_MAX_NUMBER,
  MAX_PASSENGER_NUMBER,
} from '@components/views/flight/constants';

type FlightGuest = {
  numberOfAdultGuests: number;
  numberOfChildGuests: number;
  numberOfInfantGuests: number;
  removeAdult: () => void;
  addAdult: () => void;
  removeChild: () => void;
  addChild: () => void;
  removeInfant: () => void;
  addInfant: () => void;
  reset: () => void;
  onApplyGuestsForm?: (params: FlightGuestProps) => void;
  errorMessages: string[];
};
const useFlightGuests = (
  props: Pick<
    FlightGuestBoxProps,
    'numberOfAdultGuests' | 'numberOfChildGuests' | 'numberOfInfantGuests'
  >
): FlightGuest => {
  const [numberOfAdultGuests, setNumberOfAdultGuests] =
    useState<number>(ADULT_DEFAULT_NUMBER);
  const [numberOfChildGuests, setNumberOfChildGuests] =
    useState<number>(CHILD_DEFAULT_NUMBER);
  const [numberOfInfantGuests, setNumberOfInfantGuests] = useState<number>(
    INFANT_DEFAULT_NUMBER
  );

  const { addErrorMessage, clearMessages, lastErrorMessage } =
    useGuestBoxErrorMessage();

  useEffect(() => {
    setNumberOfAdultGuests(props.numberOfAdultGuests);
  }, [props.numberOfAdultGuests]);

  useEffect(() => {
    setNumberOfChildGuests(props.numberOfChildGuests);
  }, [props.numberOfChildGuests]);

  useEffect(() => {
    setNumberOfInfantGuests(props.numberOfInfantGuests);
  }, [props.numberOfInfantGuests]);

  useEffect(() => {
    clearMessages();
  }, [numberOfAdultGuests, numberOfChildGuests, numberOfInfantGuests]);

  const removeAdult = () => {
    setNumberOfAdultGuests(numberOfAdultGuests - 1);
  };

  const addAdult = () => {
    if (numberOfAdultGuests + numberOfChildGuests >= MAX_PASSENGER_NUMBER) {
      addErrorMessage(getTotalGuestNumberErrorMessage());
    } else if (numberOfAdultGuests >= ADULT_MAX_NUMBER) {
      addErrorMessage(
        getGuestMaxNumberErrorMessage('Yetişkin', ADULT_MAX_NUMBER)
      );
    } else {
      setNumberOfAdultGuests(numberOfAdultGuests + 1);
    }
  };

  const removeChild = () => {
    try {
      setNumberOfChildGuests(numberOfChildGuests - 1);
    } catch (err) {
      console.log('removeChild', err);
    }
  };

  const addChild = () => {
    if (numberOfAdultGuests + numberOfChildGuests >= MAX_PASSENGER_NUMBER) {
      addErrorMessage(getTotalGuestNumberErrorMessage());
    } else if (numberOfChildGuests >= CHILD_MAX_NUMBER) {
      addErrorMessage(getGuestMaxNumberErrorMessage('Çocuk', CHILD_MAX_NUMBER));
    } else {
      setNumberOfChildGuests(numberOfChildGuests + 1);
    }
  };

  const removeInfant = () => {
    setNumberOfInfantGuests(numberOfInfantGuests - 1);
  };

  const addInfant = () => {
    if (numberOfInfantGuests >= numberOfAdultGuests) {
      addErrorMessage(
        'Bebek yolcu sayısı, yetişkin yolcu sayısından fazla olamaz.'
      );
    } else if (numberOfInfantGuests >= INFANT_MAX_NUMBER) {
      addErrorMessage(
        getGuestMaxNumberErrorMessage('Bebek', INFANT_MAX_NUMBER)
      );
    } else {
      setNumberOfInfantGuests(numberOfInfantGuests + 1);
    }
  };

  const reset = () => {
    setNumberOfAdultGuests(props.numberOfAdultGuests);
    setNumberOfChildGuests(props.numberOfChildGuests);
    setNumberOfInfantGuests(props.numberOfInfantGuests);
  };
  const onApplyGuestsForm = (guestInfoParam: FlightGuestProps | undefined) => {
    guestInfoParam?.numberOfAdultGuests &&
      setNumberOfAdultGuests(guestInfoParam?.numberOfAdultGuests);
    guestInfoParam?.numberOfChildGuests &&
      setNumberOfChildGuests(guestInfoParam.numberOfChildGuests);
    guestInfoParam?.numberOfInfantGuests &&
      setNumberOfInfantGuests(numberOfInfantGuests);
  };
  return {
    numberOfAdultGuests,
    numberOfChildGuests,
    numberOfInfantGuests,
    removeAdult,
    addAdult,
    removeChild,
    addChild,
    removeInfant,
    addInfant,
    errorMessages: lastErrorMessage,
    reset,
    onApplyGuestsForm,
  };
};

export default useFlightGuests;
